import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { AhfcIdentityErrorCodes, ApiException, isUnityIdentityProblem, PasswordResetsClient } from '@unity/clients';
import { TrackFormDirective } from '@unity/directives/track-form.directive';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { OutsideContainerComponent } from '@unity/layouts/outside-container/outside-container.component';
import { ToasterService } from '@unity/services/toaster.service';
import { emailFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema } from '@unity/utils/types';
import { take } from 'rxjs';

class ResetPasswordSchema implements FormGroupControlSchema<{ username: string }> {
  public username = emailFormControl();
}
type Stages_ = 'entry' | 'complete' | 'unconfirmed';

@Component({
  selector: 'unity-login-reset-password-page',
  standalone: true,
  imports: [
    KENDO_BUTTON,
    KENDO_LOADER,
    KENDO_LAYOUT,
    ReactiveFormsModule,
    TrackFormDirective,
    OutsideContainerComponent,
    RouterLink,
    TextboxControlComponent,
  ],
  templateUrl: './login-reset-password-page.component.html',
  styleUrl: './login-reset-password-page.component.scss',
})
export class LoginResetPasswordPageComponent {
  private readonly passwordResetsClient = inject(PasswordResetsClient);
  private readonly toasterService = inject(ToasterService);
  private readonly formBuilder = inject(FormBuilder);

  protected modalHeaderSubtitle = 'Password';
  protected modalHeaderTitle = 'Reset';

  protected stage: Stages_ = 'entry';
  protected resetForm = this.formBuilder.group(new ResetPasswordSchema());
  protected formLoading = false;

  protected readonly $username = toSignal(this.resetForm.controls.username.valueChanges);

  private setStage(stage: Stages_) {
    this.stage = stage;
  }

  protected onResetSubmit() {
    this.formLoading = true;
    this.passwordResetsClient
      .createOne({
        user_name: this.resetForm.controls.username.value,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          return this.setStage('complete');
        },
        error: (e: Error) => {
          if (
            ApiException.isApiException(e) &&
            isUnityIdentityProblem(e.result) &&
            e.result.error_code == AhfcIdentityErrorCodes.EmailNotConfirmed
          ) {
            this.resetForm.markAsPristine();
            return this.setStage('unconfirmed');
          }
          this.toasterService.showError('Error during Password Reset', e, 'error');
          this.formLoading = false;
        },
        complete: () => {
          this.formLoading = false;
          this.resetForm.reset();
        },
      });
  }
}
